import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CirclePlus } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { registerGeofencesData } from "@/Api/geofences";
import { getSelectUnities } from "@/Api/unities";
import { getSelectPositions } from "@/Api/positions";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AddressSearch } from "@/components/AddressSearch";
import { GeofenceMap, type GeofenceMapRef } from "../shared/GeofenceMap";

const NO_POSITION = "none";
const DEFAULT_CENTER: [number, number] = [-38.5267, -3.7327]; // Fortaleza-CE

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  unity_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  position_id: z.string().optional(),
  latitude: z.string({ message: "*requerido" }).min(1, "*requerido"),
  longitude: z.string({ message: "*requerido" }).min(1, "*requerido"),
  radius_meters: z.string({ message: "*requerido" }).min(1, "*requerido"),
});

type SchemaType = z.infer<typeof schema>;

export const AddBtn = ({ updateFront }) => {
  const [unities, setUnities] = useState([]);
  const [positions, setPositions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      if (!isOpen) {
        return;
      }
      const [{ data: unitiesData }, { data: positionsData }] =
        await Promise.all([getSelectUnities(), getSelectPositions()]);
      setUnities(unitiesData?.response || []);
      setPositions(positionsData?.response || []);
    };
    fetchOptions();
  }, [isOpen]);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      unity_id: "",
      position_id: NO_POSITION,
      latitude: "",
      longitude: "",
      radius_meters: "100",
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const formWatch = form.watch();
  const mapRef = useRef<GeofenceMapRef>(null);

  const markerLng = Number(formWatch.longitude) || DEFAULT_CENTER[0];
  const markerLat = Number(formWatch.latitude) || DEFAULT_CENTER[1];
  const hasPoint = !!formWatch.latitude && !!formWatch.longitude;
  const radius = Number(formWatch.radius_meters) || 0;

  const setPoint = (lng: number, lat: number) => {
    form.setValue("longitude", lng.toFixed(7));
    form.setValue("latitude", lat.toFixed(7));
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const payload = {
      ...data,
      position_id:
        data.position_id && data.position_id !== NO_POSITION
          ? data.position_id
          : null,
      latitude: Number(data.latitude),
      longitude: Number(data.longitude),
      radius_meters: Number(data.radius_meters),
    };

    const { data: apiResponse, ok } = await registerGeofencesData(payload);
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront((prev) => {
      if (prev?.data.length < 15)
        return {
          ...prev,
          data: [apiResponse?.response, ...prev.data],
        };

      prev?.data.pop();
      return {
        ...prev,
        data: [apiResponse?.response, ...prev.data],
        last_page: prev.last_page + 1,
      };
    });

    form.reset();
    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message,
      action: <Check color="#1edc2b" />,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Cerca Virtual
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[950px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Cerca Virtual
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Form.Row>
                <Form.Field name="name">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Nome <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input {...field} placeholder="Insira um nome" />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <Form.Row>
                <Form.Field name="unity_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Unidade <Form.Message />
                      </Form.Label>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma unidade" />
                        </SelectTrigger>
                        <SelectContent>
                          {unities.map(
                            (unity: { id: string; name: string }) => (
                              <SelectItem
                                key={unity.id}
                                value={unity.id.toString()}
                              >
                                {unity.name}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </Form.Item>
                  )}
                </Form.Field>
                <Form.Field name="position_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>Cargo (opcional)</Form.Label>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Todos os cargos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_POSITION}>
                            Todos os cargos
                          </SelectItem>
                          {positions.map(
                            (position: { id: string; name: string }) => (
                              <SelectItem
                                key={position.id}
                                value={position.id.toString()}
                              >
                                {position.name}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <Form.Row>
                <Form.Field name="radius_meters">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Raio (metros) <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input {...field} type="number" min={1} max={5000} />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">
                  Buscar endereço <span className="text-red-500">*</span>
                </label>
                <AddressSearch
                  placeholder="Digite o endereço para localizar no mapa"
                  onAddressSelect={(address) => {
                    setPoint(
                      Number(address.longitude),
                      Number(address.latitude)
                    );
                    mapRef.current?.flyTo({
                      center: [
                        Number(address.longitude),
                        Number(address.latitude),
                      ],
                      zoom: 16,
                    });
                  }}
                />
              </div>

              <Form.Row>
                <Form.Field name="latitude">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Latitude <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input {...field} placeholder="Ex: -23.5505199" />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
                <Form.Field name="longitude">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Longitude <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input {...field} placeholder="Ex: -46.6333094" />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">
                Clique no mapa ou arraste o marcador para ajustar o ponto
                central
              </label>
              <div className="h-[420px] md:h-full min-h-[420px] w-full overflow-hidden rounded-md border">
                <GeofenceMap
                  ref={mapRef}
                  latitude={markerLat}
                  longitude={markerLng}
                  radiusMeters={radius}
                  zoom={hasPoint ? 15 : 11}
                  editable
                  onPick={setPoint}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Adicionando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Adicionar"}
              {formState === "error" && "Erro ao adicionar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
